import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isIOS, isSafari } from 'react-device-detect';

import { Close } from '@material-ui/icons';
import { Divider, IconButton, List, ListItem, ListItemText, makeStyles, Menu } from '@material-ui/core';

import { focusOnFirstElement, joinClassNames } from '../../../util/util';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { usePlayer } from '../../../contexts/PlayerContext';
import { usePresenter } from '../../../contexts/PresenterContext';

import { MenuUtils } from './MenuUtils';

interface QualitySettingMenuProps {
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
	className?: string;
	onLanguageChange?: (_type: 'audio' | 'subtitle', _code: any) => void;
}

const useListCustomStyle = makeStyles(() => ({
	listItem: {
		minWidth: '190px',
		color: '#fff',
	},
	listItemMobile: {
		width: '150px',
		color: '#fff',
	},
	listItemFocus: {
		outline: '1px dash white',
	},
	listItemIcon: {
		fontSize: '1em',
	},
	primaryListText: {
		fontSize: '0.75em',
		lineHeight: 1.5,
		textTransform: 'none',
	},
	focusButton: {
		'&:focus': {
			color: '#ffffff',
		},
	},
}));

export const LanguageSettingMenu = forwardRef(
	({ playerConfigs, className, isMobile, onLanguageChange }: QualitySettingMenuProps, ref) => {
		const { t } = useTranslation();
		const firstTime = useRef<number>(0);

		const { openSettingMenu, mediasData, currentMediaId, playlistId, transcriptions, subtitleLanguage, showTranscriptionsModule, configParams, isFullScreen, setOpenSettingMenu, setShowTranscriptionsModule, setSubtitleLanguage, liveTranscriptLanguage,             isMediaLive, } = useConfiguration();
		const { playerReady, isPlaying, setShowChatWindow, setShowShoppingCart } = usePlayer();

		const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
		const [selectedSubtitle, setSelectedSubtitle] = useState<{
			code: string;
			impaired?: boolean;
		} | null>({ code: 'none' });
		const [selectedLanguage, setSelectedLanguage] = useState<{
			code: string;
			impaired?: boolean;
		} | null>({ code: 'default' });
		const [hoveringItem, setHoveringItem] = useState<{
			code: string;
			impaired?: boolean;
		} | null>(null);
		const [selectedMenu, setSelectedMenu] = useState('');
		const [selectedMenuHoveringItem, setSelectedMenuHoveringItem] = useState('');

		const playerId = useMemo(() => `player-${playlistId || currentMediaId}-${configParams.containerKey}`, [playlistId, currentMediaId, configParams.containerKey]);
		const currentMedia = useMemo(
			() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
			[mediasData, currentMediaId]
		);
		const classes = useListCustomStyle();

		const subtitleItems = useMemo(() => {
			if ((!currentMedia || (currentMedia.subtitles ?? []).length === 0) && !isMediaLive) {
				return [];
			}

			if (isMediaLive && !liveTranscriptLanguage) {
				return [];
			}

			if (isMediaLive && liveTranscriptLanguage) {
				return [
					{
						language: {
							code: liveTranscriptLanguage,
						},
						isTranscript: true,
					},
				];
			}

			const result: any[] = [];
			currentMedia.subtitles.forEach((s: any) => {
				if (!result.find((r) => r.language.code === s.language.code)) {
					result.push(s);
				}
			});

			return result;
		}, [currentMedia, isMediaLive, liveTranscriptLanguage]);

		const languageItems = useMemo(() => {
			if (!currentMedia || (currentMedia.audioTracks ?? []).length === 0) {
				return [];
			}

			const result: any[] = [];
			currentMedia.audioTracks.forEach((s: any) => {
				if (!result.find((r) => r.language.code === s.language.code && r.language.impaired === s.language.impaired)) {
					result.push(s);
				}
			});

			return result;
		}, [currentMedia]);

		const changeSubtitle = useCallback(
			(item: any) => {
				if (item?.code === selectedSubtitle?.code) {
					return;
				}
				const sub = item === 'none' ? { code: 'none' } : item;
				setSelectedSubtitle(sub);
				setSubtitleLanguage(sub);

				if (item === 'none' || sub?.code === 'none') {
					(window as any).GoBrain.widgets(playerId)?.subtitles?.(null);
				} else {
					(window as any).GoBrain.widgets(playerId)?.subtitles?.(sub.code);
				}
				onLanguageChange?.('subtitle', sub);
			},
			[selectedSubtitle?.code, setSubtitleLanguage, onLanguageChange, playerId]
		);

		const changeLanguage = useCallback(
			(item: any) => {
				if (item?.code === selectedLanguage?.code && item?.impaired === selectedLanguage?.impaired) {
					return;
				}

				setSelectedLanguage(item);
				(window as any).GoBrain.widgets(playerId).audioTrack(item);
			},
			[playerId, selectedLanguage]
		);

		const onMenuItemClick = useCallback(
			(_e: React.MouseEvent, item: any, type = 'language') => {
				if (type === 'language') {
					changeLanguage(item);
				} else {
					changeSubtitle(item);
				}
			},
			[changeSubtitle, changeLanguage]
		);

		const onCloseMenu = useCallback(() => {
			setOpenSettingMenu(null);
			setHoveringItem(null);
			setSelectedMenu('');

			if (isMobile) {
				const dialogWrapper = container.querySelector('.bottomControlbarLanguageMenu') as HTMLElement;
				dialogWrapper?.focus();
				setTimeout(() => {
					const dialogWrapper = container.querySelector('.bottomControlbarLanguageMenu') as HTMLElement;
					dialogWrapper?.focus();
				}, 100);
			}
		}, [setOpenSettingMenu, isMobile, container]);

		const onKeyDown = useCallback(
			(event, item: any, type = 'language') => {
				if (event.key === 'Tab') {
					event.stopPropagation();
					return;
				}

				if (event.key === 'Enter' || event.key === ' ') {
					if (type === 'language') {
						changeLanguage(item);
					} else {
						changeSubtitle(item);
					}
					event.stopPropagation();
				}
			},
			[changeSubtitle, changeLanguage]
		);

		const openTranscriptionModule = useCallback(() => {
			setShowTranscriptionsModule(!showTranscriptionsModule);
			setShowChatWindow(false);
			setShowShoppingCart(false);
			onCloseMenu();
		}, [onCloseMenu, setShowChatWindow, setShowShoppingCart, setShowTranscriptionsModule, showTranscriptionsModule]);

		const onTranscriptMenuKeyDown = useCallback(
			(event: any) => {
				if (event.key === 'Tab') {
					event.stopPropagation();
					return;
				}
				if (event.key === 'Enter' || event.key === ' ') {
					event.stopPropagation();
					event.preventDefault();
					openTranscriptionModule();
				}
			},
			[openTranscriptionModule]
		);

		const handleKeydownLanguageSetting = (event: any, type: any) => {
			if (event.key === 'Tab') {
				event.stopPropagation();
				return;
			}
			if (event.key === 'Enter' || event.key === ' ') {
				setSelectedMenu(type);
			}
		};

		const openSubtitleSettingMenu = useCallback(
			(event: any) => {
				if (event.key === 'Enter' || event.key === ' ' || event.type === 'click') {
					event.stopPropagation();
					event.preventDefault();
					setShowChatWindow(false);
					setShowShoppingCart(false);
					setOpenSettingMenu({
						type: 'subtitleSettings',
						anchor: event.target,
					});
					setHoveringItem(null);
					setSelectedMenu('');
				}
			},
			[setOpenSettingMenu, setShowChatWindow, setShowShoppingCart]
		);

		const onSmallScreenKeyDown = (event: any) => {
			if (event.key === 'Escape') {
				onCloseMenu();
			}
		}

		useImperativeHandle(ref, () => ({
			setSelectedMenu,
		}));

		useEffect(() => {
			if (!playerReady || isPlaying === undefined || firstTime.current >= 2) {
				return;
			}

			if (firstTime.current < 1) {
				const { language: paramLanguage, subtitlesAuto } = configParams ?? {};
				if ((!subtitlesAuto && playerConfigs?.playbackOptions?.subtitlesAutoOn) || subtitlesAuto === "true") {
					let language = paramLanguage ? paramLanguage : playerConfigs?.accessibilitySettings?.language;
					if (subtitleItems.length !== 0 && !subtitleItems.find((item: any) => item?.language?.code === language)) {
						language = 'none';
					}
					changeSubtitle({ code: language === 'dk' ? 'da' : language });
				} else {
					if (playlistId) {
						return;
					}
					const widget = (window as any).GoBrain.widgets(playerId);
					widget &&
						widget.subtitles?.().then((code: any) => {
							let language = code;
							if (subtitleItems.length !== 0 && !subtitleItems.find((item: any) => item?.language?.code === language)) {
								language = 'none';
							}
							setSelectedSubtitle({ code: language ?? 'none' });
						});
				}
			}

			if (firstTime.current < 2) {
				if (isSafari || isIOS) {
					setSelectedLanguage({ code: 'default' });
				}
			}

			firstTime.current++;
		}, [
			playerId,
			playerReady,
			configParams,
			playerConfigs?.accessibilitySettings?.language,
			playerConfigs?.playbackOptions?.subtitlesAutoOn,
			changeSubtitle,
			playlistId,
			isPlaying,
			subtitleItems
		]);

		useEffect(() => {
			if (openSettingMenu?.type === 'language' || openSettingMenu?.type === 'subtitleSettings') {
				return;
			}
			return () => {
				setHoveringItem(null);
				setSelectedMenu('');
			};
		}, [openSettingMenu]);

		useEffect(() => {
			if (subtitleLanguage?.code && subtitleLanguage?.code !== selectedSubtitle?.code) {
				const subtitleCorrespondingItem =
					subtitleItems.find((item: any) => item?.language?.code === subtitleLanguage?.code) ??
					subtitleItems.find((item: any) => item?.language?.code?.includes(subtitleLanguage?.code));
				subtitleCorrespondingItem?.language && changeSubtitle(subtitleCorrespondingItem?.language);
			}
		}, [changeSubtitle, selectedSubtitle, subtitleItems, subtitleLanguage]);

		const focusOnDialog = useCallback(() => {
			if (!isMobile) {
				return;
			}
			setTimeout(() => {
				const dialogWrapper = container.querySelector('#language-dialog') as HTMLElement;
				dialogWrapper?.focus();
			}, 200);
		}, [isMobile, container]);

		const onGoBack = useCallback(() => {
			setSelectedMenu('');
			focusOnDialog();
		}, [focusOnDialog]);

		useEffect(() => {
			if (openSettingMenu?.type === 'language') {
				focusOnDialog();
			}
		}, [focusOnDialog, isMobile, openSettingMenu, selectedMenu]);

		useEffect(() => {
			if (openSettingMenu?.type !== 'language') {
				return;
			}

			if (openSettingMenu?.isKeyboardTriggered) {
				focusOnFirstElement();
			} else {
				setSelectedMenuHoveringItem('');
			}
		}, [openSettingMenu?.type, selectedMenu, openSettingMenu?.isKeyboardTriggered]);

		const renderSettingMenuItems = () => {
			const listItemClass = classes.listItem;
			const showAllMenus = [languageItems, subtitleItems, transcriptions?.entries].filter(Boolean).filter((item) => item.length > 0).length > 1;

			return (
				<>
					{!selectedMenu && showAllMenus && (
						<>
							{isMobile && (
								<>
									<ListItem className={listItemClass} component={'div'} autoFocus>
										<ListItemText
											classes={{ primary: classes.primaryListText }}
											primary={t('AUDIO_SUBTITLES')}
											id="language-sub-header"
										/>
										<IconButton
											aria-label={t('LABEL_CLOSE')}
											style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
											onClick={onCloseMenu}
											className={classes.focusButton}
										>
											<Close className={joinClassNames(classes.listItemIcon, isSafari ? 'safari-fix' : '')} htmlColor="#fff" aria-hidden="true" />
										</IconButton>
									</ListItem>
									<Divider
										tabIndex={-1}
										aria-hidden="true"
										style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }}
									/>
								</>
							)}

							<List
								className={isMobile ? 'qplayer__setting-menu--mobile-scrollable' : ''}
								role="menu"
								aria-labelledby={'language-sub-header'}
							>
								{languageItems.length > 0 && (
									<MenuUtils.MenuSelector
										menuType="language"
										itemLabel={t('AUDIO_LANGUAGE')}
										isMobile={isMobile}
										hoveringItem={selectedMenuHoveringItem}
										playerConfigs={playerConfigs}
										onSelectMenu={setSelectedMenu}
										onKeyDown={handleKeydownLanguageSetting}
										onCloseMenu={onCloseMenu}
										onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
										onLeaveItem={() => setSelectedMenuHoveringItem('')}
									/>
								)}
								{subtitleItems.length > 0 && (
									<MenuUtils.MenuSelector
										menuType="subtitle"
										itemLabel={t('SUBTITLES')}
										isMobile={isMobile}
										hoveringItem={selectedMenuHoveringItem}
										playerConfigs={playerConfigs}
										onSelectMenu={setSelectedMenu}
										onKeyDown={handleKeydownLanguageSetting}
										onCloseMenu={onCloseMenu}
										onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
										onLeaveItem={() => setSelectedMenuHoveringItem('')}
									/>
								)}
								{(transcriptions?.entries ?? []).length > 0 && playerConfigs?.bottomControls?.showTranscriptSettings && (
									<MenuUtils.MenuSelector
										menuType="transcription"
										itemLabel={t('TRANSCRIPTION')}
										isMobile={isMobile}
										hoveringItem={selectedMenuHoveringItem}
										playerConfigs={playerConfigs}
										onSelectMenu={openTranscriptionModule}
										onKeyDown={onTranscriptMenuKeyDown}
										onCloseMenu={onCloseMenu}
										onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
										onLeaveItem={() => setSelectedMenuHoveringItem('')}
									/>
								)}
							</List>
						</>
					)}

					{languageItems.length > 0 &&
						(selectedMenu === 'language' || (subtitleItems.length === 0 && (transcriptions?.entries ?? []).length === 0)) && (
							<MenuUtils.MenuPanel
								menuType="language"
								items={languageItems}
								isMobile={isMobile}
								hoveringItem={hoveringItem}
								selectedItem={selectedLanguage}
								playerConfigs={playerConfigs}
								showAllMenus={showAllMenus}
								selectedMenu={selectedMenu}
								onSelectMenu={setSelectedMenu}
								onMenuItemClick={onMenuItemClick}
								onKeyDown={onKeyDown}
								onCloseMenu={onCloseMenu}
								onHoverItem={(lang: any) => setHoveringItem(lang)}
								onLeaveItem={() => setHoveringItem(null)}
								onGoBack={onGoBack}
								container={container}
							/>
						)}

					{subtitleItems.length > 0 &&
						(selectedMenu === 'subtitle' || (languageItems.length === 0 && (transcriptions?.entries ?? []).length === 0)) && (
							<MenuUtils.MenuPanel
								menuType="subtitle"
								items={subtitleItems}
								isMobile={isMobile}
								hoveringItem={hoveringItem}
								selectedItem={selectedSubtitle}
								playerConfigs={playerConfigs}
								showAllMenus={showAllMenus}
								selectedMenu={selectedMenu}
								isMediaLive={isMediaLive}
								onSelectMenu={setSelectedMenu}
								onMenuItemClick={onMenuItemClick}
								onKeyDown={onKeyDown}
								onCloseMenu={onCloseMenu}
								onHoverItem={(lang: any) => setHoveringItem(lang)}
								onLeaveItem={() => setHoveringItem(null)}
								onGoBack={onGoBack}
								openSubtitleSettingMenu={openSubtitleSettingMenu}
								container={container}
							/>
						)}

					{(transcriptions?.entries ?? []).length > 0 &&
						playerConfigs?.bottomControls?.showTranscriptSettings &&
						(selectedMenu === 'transcription' || (languageItems.length === 0 && subtitleItems.length === 0)) && (
							<MenuUtils.MenuSelector
								menuType="transcription"
								itemLabel={t('TRANSCRIPTION')}
								isMobile={isMobile}
								hoveringItem={selectedMenuHoveringItem}
								playerConfigs={playerConfigs}
								onSelectMenu={openTranscriptionModule}
								onKeyDown={onTranscriptMenuKeyDown}
								onCloseMenu={onCloseMenu}
								onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
								onLeaveItem={() => setSelectedMenuHoveringItem('')}
							/>
						)}
				</>
			);
		};

		if (isMobile) {
			return (
				<dialog
					id="language-dialog"
					className="qplayer__setting-menu qplayer__setting-menu--mobile-wrapper"
					onClose={onCloseMenu}
					tabIndex={0}
					open={openSettingMenu?.type === 'language'}
					aria-modal="true"
					aria-hidden={openSettingMenu?.type !== 'language'}
					onKeyDown={onSmallScreenKeyDown}
				>
					<div className="qplayer__setting-menu--mobile" onMouseDown={(e) => e.stopPropagation()}>
						{renderSettingMenuItems()}
					</div>
				</dialog>
			);
		}

		const anchorElem = container.querySelector('.bottomControlbarLanguageMenu') ?? openSettingMenu?.anchor ?? container.querySelector('.bottomControlbar');
		const bottomBar = container.querySelector('.bottomControlbar');
		let menuMaxHeight = "calc(100vh - 100px)";
		if (!configParams?.qplayerPreview) {
			const playerWindow = configParams.container.querySelector(".playerWindow");
			const qplayerHeight = playerWindow ? playerWindow.getBoundingClientRect()?.height || 0 : 0;
			menuMaxHeight = `calc(${qplayerHeight}px - ${bottomBar ? bottomBar.getBoundingClientRect()?.height + 30 : 100}px)`;
		}

		return <Menu
			onMouseDown={(e) => e.stopPropagation()}
			style={{ zIndex: configParams?.isModalInstance ? 90000 : 30001, }}
			key={'language-menu'}
			className={joinClassNames('qplayer__setting-menu', className)}
			open={openSettingMenu?.type === 'language'}
			onClose={onCloseMenu}
			getContentAnchorEl={null}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			anchorEl={anchorElem || openSettingMenu?.anchor || bottomBar}
			disablePortal={!(configParams.isModalInstance && !isFullScreen)}
			PaperProps={{
				style: {
					transform: 'translateY(-30px)',
					maxHeight: menuMaxHeight
				},
				className: 'closeable-by-tab',
			}}
			MenuListProps={{
				'aria-labelledby': 'language-sub-header',
			}}
		>
			<div
				tabIndex={-1}
				onKeyDown={(e) => {
					if (e.key.includes('Arrow')) {
						e.stopPropagation();
						e.preventDefault();
					}
				}}
			>
				{renderSettingMenuItems()}
			</div>
		</Menu>;
	}
);
